/**
 * NEO customization: opt-in Flickity scroll motion.
 * Setting: settings.neo_flickity_scroll_motion_enabled (layout/theme.liquid)
 * Targets: grid-slider[data-neo-scroll-motion]
 * Remove with the matching theme.liquid asset connection and section data attributes.
 */
(() => {
  const neo_selector = 'grid-slider[data-neo-scroll-motion]';
  const neo_section_selector = '.drb-grip-slider, .drb-products-tabs';
  const neo_min_width = 768;
  const neo_offset_ratio = 0.1;
  const neo_duration = 450;
  // DRB: after the nudge, slide back so the first card is never left cut off (0 = stay nudged like before)
  const neo_return_delay = 650;
  const neo_down_trigger_ratio = 0.5;
  const neo_up_trigger_ratio = 0.6;
  const neo_initialized = new WeakSet();
  const neo_states = new WeakMap();
  const neo_motion_query = window.matchMedia('(prefers-reduced-motion: reduce)');

  let neo_last_scroll_y = window.scrollY;
  let neo_scroll_frame = null;

  const neo_reduced_motion = () => neo_motion_query.matches;

  const neo_get_flickity = (neo_slider) => {
    if (!window.theme?.Flickity || !neo_slider.classList.contains('flickity-enabled')) return null;

    const neo_flickity = window.theme.Flickity.data(neo_slider);
    return neo_flickity?.isActive ? neo_flickity : null;
  };

  const neo_get_state = (neo_slider) => {
    let neo_state = neo_states.get(neo_slider);

    if (!neo_state) {
      neo_state = {
        animation_frame: null,
        offset: 0,
        user_interacted: false,
      };
      neo_states.set(neo_slider, neo_state);
    }

    return neo_state;
  };

  const neo_cancel_animation = (neo_state) => {
    if (neo_state.return_timer) {
      clearTimeout(neo_state.return_timer);
      neo_state.return_timer = null;
    }
    if (neo_state.animation_frame !== null) {
      cancelAnimationFrame(neo_state.animation_frame);
      neo_state.animation_frame = null;
    }
  };

  const neo_restore_flickity = (neo_slider, neo_state) => {
    neo_cancel_animation(neo_state);

    const neo_flickity = neo_get_flickity(neo_slider);
    if (neo_flickity) neo_flickity.positionSlider();

    neo_state.offset = 0;
  };

  const neo_mark_interacted = (neo_slider) => {
    const neo_state = neo_get_state(neo_slider);
    const neo_flickity = neo_get_flickity(neo_slider);

    neo_cancel_animation(neo_state);

    // Keep the visual nudge as Flickity's real starting point so controls do not jump through 0%.
    if (neo_flickity && neo_state.offset !== 0) {
      neo_flickity.x += neo_state.offset;
      neo_flickity.velocity = 0;
      neo_flickity.positionSlider();
      neo_state.offset = 0;
    }

    neo_state.user_interacted = true;
  };

  const neo_reset_slider = (neo_slider) => {
    const neo_state = neo_get_state(neo_slider);
    const neo_flickity = neo_get_flickity(neo_slider);
    const neo_was_user_interacted = neo_state.user_interacted;

    neo_cancel_animation(neo_state);
    neo_state.user_interacted = false;

    if (!neo_flickity) {
      neo_state.offset = 0;
      return;
    }

    if (neo_was_user_interacted || neo_flickity.selectedIndex !== 0) {
      neo_state.offset = 0;
      neo_flickity.select(0);
      return;
    }

    neo_animate(neo_slider, 0);
  };

  const neo_animate = (neo_slider, neo_target_offset) => {
    const neo_state = neo_get_state(neo_slider);
    const neo_flickity = neo_get_flickity(neo_slider);

    if (
      !neo_flickity ||
      neo_state.user_interacted ||
      neo_flickity.selectedIndex !== 0 ||
      neo_flickity.isPointerDown ||
      neo_flickity.isAnimating
    ) {
      return;
    }

    neo_cancel_animation(neo_state);

    const neo_start_offset = neo_state.offset;
    const neo_offset_delta = neo_target_offset - neo_start_offset;
    const neo_base_x = neo_flickity.x;
    const neo_started_at = performance.now();

    const neo_step = (neo_now) => {
      const neo_live_flickity = neo_get_flickity(neo_slider);

      if (!neo_live_flickity || neo_state.user_interacted || neo_live_flickity.selectedIndex !== 0) {
        neo_restore_flickity(neo_slider, neo_state);
        return;
      }

      const neo_progress = Math.min((neo_now - neo_started_at) / neo_duration, 1);
      const neo_eased_progress = 1 - Math.pow(1 - neo_progress, 3);
      const neo_current_offset = neo_start_offset + neo_offset_delta * neo_eased_progress;

      neo_live_flickity.setTranslateX(neo_base_x + neo_current_offset, true);
      neo_state.offset = neo_current_offset;

      if (neo_progress < 1) {
        neo_state.animation_frame = requestAnimationFrame(neo_step);
      } else {
        neo_state.animation_frame = null;
        if (neo_return_delay > 0 && neo_target_offset !== 0) {
          neo_state.return_timer = setTimeout(() => {
            neo_state.return_timer = null;
            if (!neo_state.user_interacted) neo_animate(neo_slider, 0);
          }, neo_return_delay);
        }
      }
    };

    neo_state.animation_frame = requestAnimationFrame(neo_step);
  };

  const neo_initialize_slider = (neo_slider) => {
    if (neo_initialized.has(neo_slider)) return;

    neo_initialized.add(neo_slider);
    neo_get_state(neo_slider);

    neo_slider.addEventListener('pointerdown', () => neo_mark_interacted(neo_slider), {
      capture: true,
      passive: true,
    });
    neo_slider.addEventListener(
      'keydown',
      (neo_event) => {
        if (neo_event.key === 'ArrowLeft' || neo_event.key === 'ArrowRight') {
          neo_mark_interacted(neo_slider);
        }
      },
      { capture: true }
    );
    neo_slider.addEventListener(
      'click',
      (neo_event) => {
        if (neo_event.target.closest('.flickity-prev-next-button')) {
          neo_mark_interacted(neo_slider);
        }
      },
      { capture: true }
    );
    neo_slider.addEventListener('theme:tab:change', () => {
      const neo_state = neo_get_state(neo_slider);
      neo_cancel_animation(neo_state);
      neo_state.offset = 0;
      neo_state.user_interacted = false;

      requestAnimationFrame(() => {
        const neo_section = neo_slider.closest(neo_section_selector);
        if (!neo_section || neo_section.dataset.neoScrollMotionActive !== 'true') return;

        const neo_flickity = neo_get_flickity(neo_slider);
        if (neo_flickity) {
          neo_animate(neo_slider, -neo_flickity.size.innerWidth * neo_offset_ratio);
        }
      });
    });
  };

  const neo_initialize = (neo_root = document) => {
    if (neo_root.matches?.(neo_selector)) neo_initialize_slider(neo_root);
    neo_root.querySelectorAll?.(neo_selector).forEach(neo_initialize_slider);
  };

  const neo_update = () => {
    neo_scroll_frame = null;

    const neo_current_scroll_y = window.scrollY;
    const neo_direction = neo_current_scroll_y > neo_last_scroll_y ? 'down' : neo_current_scroll_y < neo_last_scroll_y ? 'up' : null;
    neo_last_scroll_y = neo_current_scroll_y;

    if (!neo_direction || window.innerWidth < neo_min_width || neo_reduced_motion()) return;

    document.querySelectorAll(neo_section_selector).forEach((neo_section) => {
      const neo_rect = neo_section.getBoundingClientRect();
      const neo_down_trigger = window.innerHeight * neo_down_trigger_ratio;
      const neo_up_trigger = window.innerHeight * neo_up_trigger_ratio;
      const neo_is_active = neo_section.dataset.neoScrollMotionActive === 'true';

      if (neo_direction === 'down' && !neo_is_active && neo_rect.top <= neo_down_trigger && neo_rect.bottom > 0) {
        neo_section.dataset.neoScrollMotionActive = 'true';

        neo_section.querySelectorAll(neo_selector).forEach((neo_slider) => {
          if (neo_slider.offsetParent === null) return;
          neo_initialize_slider(neo_slider);

          const neo_flickity = neo_get_flickity(neo_slider);
          if (neo_flickity) neo_animate(neo_slider, -neo_flickity.size.innerWidth * neo_offset_ratio);
        });
      }

      if (neo_direction === 'up' && neo_is_active && neo_rect.top >= neo_up_trigger) {
        neo_section.dataset.neoScrollMotionActive = 'false';

        neo_section.querySelectorAll(neo_selector).forEach((neo_slider) => {
          neo_reset_slider(neo_slider);
        });
      }
    });
  };

  const neo_on_scroll = () => {
    if (neo_scroll_frame === null) neo_scroll_frame = requestAnimationFrame(neo_update);
  };

  const neo_reset_motion = () => {
    document.querySelectorAll(neo_selector).forEach((neo_slider) => {
      neo_restore_flickity(neo_slider, neo_get_state(neo_slider));
    });

    document.querySelectorAll(neo_section_selector).forEach((neo_section) => {
      neo_section.dataset.neoScrollMotionActive = 'false';
    });
  };

  neo_initialize();
  window.addEventListener('scroll', neo_on_scroll, { passive: true });
  document.addEventListener('theme:resize:width', neo_reset_motion);
  neo_motion_query.addEventListener?.('change', neo_reset_motion);
  document.addEventListener('shopify:section:load', (neo_event) => neo_initialize(neo_event.target));
})();
